import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { ArrowLeft } from "lucide-react";

function Register() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, user } = useAuth();
  const navigate = useNavigate();
  const recaptchaRef = useRef();

  useEffect(() => {
    if (user) {
      navigate("/community");
    }
  }, [user, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-=\[\]{}|;:',.<>?]{8,128}$/;
    return strongPasswordRegex.test(password);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!validatePassword(password)) {
      setError(
        "Password must be 8–128 characters and include at least one uppercase letter, one lowercase letter, and one number."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!acceptedTerms) {
      setError("You must accept the terms and conditions to register.");
      return;
    }

    if (!recaptchaToken) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    try {
      await register(email, password, username, recaptchaToken);
      navigate("/community");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to register. Please try again.";
      setError(msg);

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-10">
      {/* BACK TO HOME */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-3 left-3 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 sm:gap-2
                   rounded-lg sm:rounded-xl border border-emerald-500/30
                   bg-emerald-500/10 backdrop-blur
                   px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium
                   text-emerald-300 hover:bg-emerald-500/15
                   transition active:scale-95"
      >
        <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
        <span className="hidden xs:inline">Back to Home</span>
        <span className="xs:hidden">Back</span>
      </button>

      <div className="max-w-5xl w-full grid gap-6 sm:gap-8 md:gap-10 md:grid-cols-[1.1fr,1fr] items-center">
        {/* LEFT SECTION */}
        <div className="space-y-4 sm:space-y-5 md:space-y-6 text-slate-100">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-medium text-emerald-300">
            <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-400 animate-pulse" />
            Animehouse • Community Hub
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">
            Join the{" "}
            <span className="text-emerald-400">Animehouse community</span>
          </h1>

          <p className="text-xs sm:text-sm md:text-base text-slate-400 max-w-md">
            Create your profile, join live watch parties, share fan art, and earn
            XP from posts and replies.
          </p>

          <div className="grid grid-cols-1 xs:grid-cols-3 gap-2.5 sm:gap-3 md:gap-4 max-w-md text-xs md:text-sm">
            <div className="rounded-xl sm:rounded-2xl bg-slate-900/70 border border-emerald-500/15 px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-slate-400 text-[10px] sm:text-xs">Live parties</p>
              <p className="text-base sm:text-lg font-semibold text-emerald-300">Real-time</p>
            </div>
            <div className="rounded-xl sm:rounded-2xl bg-slate-900/70 border border-emerald-500/15 px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-slate-400 text-[10px] sm:text-xs">Threads</p>
              <p className="text-base sm:text-lg font-semibold text-emerald-300">Hot takes</p>
            </div>
            <div className="rounded-xl sm:rounded-2xl bg-slate-900/70 border border-emerald-500/15 px-3 sm:px-4 py-2.5 sm:py-3">
              <p className="text-slate-400 text-[10px] sm:text-xs">XP & badges</p>
              <p className="text-base sm:text-lg font-semibold text-emerald-300">Soon</p>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
            >
              Log in instead
            </Link>
            .
          </p>
        </div>

        {/* RIGHT — FORM */}
        <div className="bg-slate-950/80 border border-emerald-500/20 shadow-xl shadow-emerald-500/15 rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-7 backdrop-blur">
          <div className="mb-4 sm:mb-5">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-100">
              Create your account
            </h2>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
              Pick a unique username — this is how you'll appear in threads.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-3">
            {/* USERNAME */}
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
              className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-emerald-400 transition"
            />

            {/* EMAIL */}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-emerald-400 transition"
            />

            {/* PASSWORD + CONFIRM PASSWORD */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* PASSWORD */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2.5 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-emerald-400 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-emerald-300 transition"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 3l18 18M9.9 9.9A3 3 0 0112 9c1.66 0 3 1.34 3 3 0 .51-.13.98-.35 1.4M6.4 6.4C4.35 7.84 3 9.79 3 12c0 4 5 8 9 8 1.52 0 2.97-.39 4.24-1.06M14.1 14.1A3 3 0 0112 15c-1.66 0-3-1.34-3-3 0-.51.13-.98.35-1.4"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M2.036 12.322C3.423 7.51 7.36 4 12 4c4.64 0 8.577 3.51 9.964 8.322a1.03 1.03 0 010 .356C20.577 16.49 16.64 20 12 20c-4.64 0-8.577-3.51-9.964-8.322a1.03 1.03 0 010-.356z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {/* CONFIRM PASSWORD */}
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  className="w-full rounded-xl border border-slate-700 bg-slate-900/80 px-3 py-2.5 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:border-emerald-400 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-emerald-300 transition"
                >
                  {showConfirmPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 3l18 18M9.9 9.9A3 3 0 0112 9c1.66 0 3 1.34 3 3 0 .51-.13.98-.35 1.4M6.4 6.4C4.35 7.84 3 9.79 3 12c0 4 5 8 9 8 1.52 0 2.97-.39 4.24-1.06M14.1 14.1A3 3 0 0112 15c-1.66 0-3-1.34-3-3 0-.51.13-.98.35-1.4"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M2.036 12.322C3.423 7.51 7.36 4 12 4c4.64 0 8.577 3.51 9.964 8.322a1.03 1.03 0 010 .356C20.577 16.49 16.64 20 12 20c-4.64 0-8.577-3.51-9.964-8.322a1.03 1.03 0 010-.356z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* PASSWORD RULE TEXT */}
            <p className="text-[11px] text-slate-500">
              Use at least 8 characters, including an uppercase letter, a lowercase letter, and a number.
            </p>

            {/* TERMS */}
            <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-400">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-600 bg-slate-900 checked:bg-emerald-500 checked:border-emerald-500"
              />
              <span>
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {/* RECAPTCHA */}
            <div className="mt-3 rounded-2xl bg-slate-900/70 border border-slate-700/80 px-3 py-2 flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                theme="dark"
                onChange={(token) => setRecaptchaToken(token)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </div>

            {/* BUTTON */}
            <button
              type="submit"
              className="mt-4 w-full rounded-xl bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-emerald-500/40 hover:bg-emerald-400 transition active:scale-[0.98]"
            >
              Create account
            </button>
          </form>

          <p className="mt-4 text-[11px] text-slate-500 text-center">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
